import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import {
  IntakeBuyer, IntakeInUseReason, IntakeMail, IntakeMatchCandidate, IntakeNote, IntakeVacancyFields, intakeFields,
} from '../../interfaces/intake';
import { MarketVacancy } from '../../interfaces/market-vacancy';
import { IntakeService, intakeContentGone, intakeErrorMessage } from '../../shared/intake.service';
import { IntakeDiffView, intakeDiffRows, intakeFieldLabel, intakeUnchangedLabels } from '../intake-update-diff';
import { NotifierService } from '../../shared/notifier.service';
import { CreateVacancyDialogComponent } from '../../market-vacancies/create-vacancy-dialog/create-vacancy-dialog.component';
import { NewClientDialogComponent } from '../../clients/new-client-dialog/new-client-dialog.component';

const INDEXED_NOTES = [
  'attachment_over_count', 'attachment_over_size', 'attachment_type_refused', 'attachment_ole_not_read',
  'attachment_cap_entries', 'attachment_cap_bytes', 'attachment_cap_chars_file', 'attachment_cap_time', 'attachment_unreadable',
];

/** D1 note indexes are 1-based; a note that needs one but has none (PHP dropped the parts itself) gets index-less wording. */
export function intakeNoteKey(n: IntakeNote): string {
  if (n.index == null && INDEXED_NOTES.includes(n.code)) {
    return n.code === 'attachment_over_count' ? 'intake.note.attachment_over_count_all' : 'intake.note.attachment_any';
  }
  return 'intake.note.' + n.code;
}

export function intakeNoteParams(translate: TranslateService, n: IntakeNote): { index: number; field: string } {
  return { index: n.index, field: n.field ? translate.instant(intakeFieldLabel(n.field)) : n.field };
}

export type IntakePanelCard =
  'over_cap' | 'burst' | 'parser_unavailable' | 'ambiguous_match' | 'ambiguous_verdict'
  | 'direct' | 'needs_details' | 'duplicate' | 'update' | 'not_a_role' | 'system' | 'pending' | 'expired';

/**
 * Which card the panel shows. Once the content is gone only a Direct request or a duplicate keeps its
 * card: their actions work on the stored vacancy, not on the mail. Everything else needs the content.
 */
export function intakePanelCard(mail: IntakeMail): IntakePanelCard {
  const keepsCard = mail.verdict === 'direct_request' || mail.verdict === 'duplicate';
  if (intakeContentGone(mail) && !keepsCard) { return 'expired'; }
  switch (mail.verdict) {
    case 'held': return mail.held_reason ?? 'ambiguous_verdict';
    case 'direct_request': return mail.vacancy?.needs_details ? 'needs_details' : 'direct';
    case 'duplicate': return 'duplicate';
    case 'update_candidate': return 'update';
    case 'not_a_role': return 'not_a_role';
    case 'system': return 'system';
    case 'pending': return 'pending';
    default: return 'expired';
  }
}

export function intakeOverCapText(translate: TranslateService, mail: IntakeMail): string {
  const used = mail?.usage?.used;
  const cap = mail?.usage?.cap;
  return typeof used === 'number' && typeof cap === 'number'
    ? translate.instant('intake.panel.over_cap_body', { used, cap })
    : translate.instant('intake.panel.over_cap_body_unknown');
}

function strOrNull(v: unknown): string | null {
  return v === null || v === undefined || v === '' ? null : String(v);
}

/** Dialog prefill from the vacancy (edit) or the extracted fields (create). */
export function intakeInitialValues(source: Record<string, any> | null): Partial<IntakeVacancyFields> {
  const s = source ?? {};
  return {
    title: strOrNull(s.title) ?? '',
    company: strOrNull(s.company),
    location: strOrNull(s.location),
    description: strOrNull(s.description),
    deadline: strOrNull(s.deadline),
    start_date: strOrNull(s.start_date),
    hours_per_week: strOrNull(s.hours_per_week),
    tariff: strOrNull(s.tariff),
  };
}

const IN_USE_REASONS: IntakeInUseReason[] = ['project', 'opportunity', 'submission', 'interest'];

/** 409 vacancy_in_use: the translated message plus what holds the vacancy (D1 `data.reasons`). */
export function intakeInUseMessage(translate: TranslateService, e: any): string {
  const raw = e?.error?.data?.reasons;
  const reasons = (Array.isArray(raw) ? raw : []).filter(r => IN_USE_REASONS.includes(r));
  const base = intakeErrorMessage(translate, e);
  return reasons.length
    ? `${base} ${reasons.map(r => translate.instant(`intake.panel.in_use_reason.${r}`)).join(', ')}`
    : base;
}

/** D1 match_candidates, deduplicated, positive integer ids only. */
function cleanCandidates(raw: unknown): IntakeMatchCandidate[] {
  const seen = new Set<number>();
  return (Array.isArray(raw) ? raw : []).filter(c => {
    const ok = Number.isSafeInteger(c?.id) && c.id > 0 && !seen.has(c.id);
    if (ok) { seen.add(c.id); }
    return ok;
  });
}

export interface IntakeFieldRow {
  key: string;
  label: string;
  value: string | null;
  date: boolean;
  /** Notes D1 tied to this field; they show under its row. */
  notes: IntakeNote[];
}

const FIELD_ROWS: { key: string; label: string; date: boolean }[] = [
  { key: 'title', label: 'intake.panel.f_title', date: false },
  { key: 'company', label: 'intake.panel.f_company', date: false },
  { key: 'deadline', label: 'intake.panel.f_deadline', date: true },
  { key: 'start_date', label: 'intake.panel.f_start_date', date: true },
  { key: 'hours_per_week', label: 'intake.panel.f_hours', date: false },
  { key: 'tariff', label: 'intake.panel.f_tariff', date: false },
  { key: 'location', label: 'intake.panel.f_location', date: false },
];

@Component({
  selector: 'bst-intake-verdict-panel',
  templateUrl: './intake-verdict-panel.component.html',
  styleUrls: ['./intake-verdict-panel.component.scss'],
  standalone: false
})
export class IntakeVerdictPanelComponent implements OnChanges {
  @Input() mail: IntakeMail;
  @Output() mailChange = new EventEmitter<IntakeMail>();

  busy = false;
  vacancy: MarketVacancy | null = null;
  buyers: IntakeBuyer[] = [];
  selectedBuyer: number | null = null;
  candidates: IntakeMatchCandidate[] = [];
  chosen: number | 'none' | null = null;

  /** D1 answers at most 50 buyers; at that size the hint asks the user to type. */
  readonly buyerPage = 50;

  private refreshGeneration = 0;
  private buyerGeneration = 0;

  constructor(private intake: IntakeService,
              private notifier: NotifierService,
              private translate: TranslateService,
              private dialog: NbDialogService,
              private toastr: NbToastrService) {}

  get card(): IntakePanelCard | null {
    return this.mail ? intakePanelCard(this.mail) : null;
  }

  /** The section heading is the verdict itself: a held mail names its reason, a handled update its state. */
  get heading(): string {
    const m = this.mail;
    if (!m) { return ''; }
    if (m.verdict === 'held') { return `intake.held.${m.held_reason ?? 'ambiguous_verdict'}`; }
    if (m.verdict === 'update_candidate' && m.update_state) { return `intake.update_state.${m.update_state}`; }
    return `intake.verdict.${m.verdict}`;
  }

  /** Parser notes; they describe the mail, so they go once its content is removed. */
  get notes(): IntakeNote[] {
    return this.mail && !intakeContentGone(this.mail) ? (this.mail.notes ?? []) : [];
  }

  /** Notes with no row to sit under: every note outside a Direct request, else those of fields the list does not show. */
  get looseNotes(): IntakeNote[] {
    const listed = this.card === 'direct' || this.card === 'needs_details';
    return this.notes.filter(n => !(listed && n.field && FIELD_ROWS.some(f => f.key === n.field)));
  }

  noteKey(n: IntakeNote): string {
    return intakeNoteKey(n);
  }

  noteParams(n: IntakeNote): { index: number; field: string } {
    return intakeNoteParams(this.translate, n);
  }

  /** D1 lock() answers 409 state_changed to every overturn once a content wipe has started. */
  get canOverturn(): boolean {
    return this.mail?.content_wipe_state === 'none';
  }

  get overCapText(): string {
    return intakeOverCapText(this.translate, this.mail);
  }

  /**
   * The Direct request's fields: the stored vacancy when it loaded, else what the parser extracted.
   * An empty field still gets its row when a note names it, so the note has a place under it.
   */
  get fieldRows(): IntakeFieldRow[] {
    const source: Record<string, any> = this.vacancy ?? (this.mail ? intakeFields(this.mail) : {});
    const notes = this.notes;
    return FIELD_ROWS
      .map(f => ({ key: f.key, label: f.label, value: strOrNull(source[f.key]), date: f.date, notes: notes.filter(n => n.field === f.key) }))
      .filter(r => r.value !== null || r.notes.length);
  }

  /** The buyer search page, plus the linked buyer when it is not on that page (D1 sends only its id). */
  get buyerOptions(): IntakeBuyer[] {
    const linked = this.mail?.vacancy?.buyer_Client_id;
    if (!linked || this.buyers.some(b => b.id === linked)) { return this.buyers; }
    const name = this.mail.vacancy.buyer_name || this.translate.instant('intake.panel.crm_client_linked', { id: linked });
    return [{ id: linked, name, status: null, crm_type: null } as IntakeBuyer, ...this.buyers];
  }

  /** The picker input shows the linked buyer's name; typing searches, choosing an option links it. */
  get buyerLabel(): string {
    return this.buyerName(this.selectedBuyer);
  }

  readonly buyerName = (id: number | null): string => this.buyerOptions.find(b => b.id === id)?.name ?? '';

  pickBuyer(id: number): void {
    this.selectedBuyer = id;
    void this.setBuyer(id);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.mail) {
      void this.refresh();
    }
  }

  async refresh(): Promise<void> {
    const generation = ++this.refreshGeneration;
    const mail = this.mail;
    this.vacancy = null;
    this.candidates = [];
    this.chosen = null;
    if (!mail) { return; }
    const card = intakePanelCard(mail);
    const isDirect = card === 'direct' || card === 'needs_details';
    // Candidates come with the mail (D1 match_candidates); no request per candidate.
    if (card === 'ambiguous_match') {
      this.candidates = cleanCandidates(mail.match_candidates);
      this.chosen = this.candidates.length ? null : 'none';
    }
    const vacancyId = mail.vacancy?.id;
    // The full vacancy is needed only for the edit prefill and the field list; the ref covers the rest.
    const needsVacancy = (isDirect || card === 'update') && !!vacancyId;
    const [vacancy, buyers] = await Promise.all([
      needsVacancy ? this.intake.vacancyQuietly(vacancyId) : Promise.resolve(null),
      isDirect ? this.intake.buyers().catch(() => [] as IntakeBuyer[]) : Promise.resolve(this.buyers),
    ]);
    if (generation !== this.refreshGeneration) { return; }
    this.vacancy = vacancy;
    this.buyers = buyers ?? [];
    this.selectedBuyer = mail.vacancy?.buyer_Client_id ?? null;
  }

  /** Typed search in the buyer picker (D1 `?q=`, at most 50 rows). A stale answer is dropped. */
  async searchBuyers(q: string): Promise<void> {
    const generation = ++this.buyerGeneration;
    const found = await this.intake.buyers(q).catch(() => null);
    if (found && generation === this.buyerGeneration) {
      this.buyers = found;
    }
  }

  /** One action at a time; busy always resets; only a fresh mail is emitted. */
  async run(action: () => Promise<IntakeMail | null>): Promise<void> {
    if (this.busy) { return; }
    this.busy = true;
    try {
      const fresh = await action().catch(e => this.actionFailed(e));
      if (fresh) {
        this.mailChange.emit(fresh);
      }
    } finally {
      this.busy = false;
    }
  }

  /**
   * IntakeService keeps 409 and 422 silent, so they are translated here. state_changed means
   * someone else acted first: reload the mail so the panel shows the state that now holds.
   */
  private async actionFailed(e: any, title = this.translate.instant('intake.title')): Promise<IntakeMail | null> {
    if (e?.status !== 409 && e?.status !== 422) { return null; }
    this.toastr.danger(intakeErrorMessage(this.translate, e), title);
    if (e?.error?.message === 'intake.errors.state_changed') {
      return this.intake.mail(this.mail.id).catch(() => null);
    }
    return null;
  }

  parseNow(): Promise<void> {
    return this.run(() => this.intake.parseNow(this.mail.id));
  }

  release(): Promise<void> {
    return this.run(() => this.intake.release(this.mail.id));
  }

  retry(): Promise<void> {
    return this.run(() => this.intake.retry(this.mail.id));
  }

  get updateRows(): IntakeDiffView[] {
    return intakeDiffRows(this.mail?.update_diff);
  }

  get unchangedText(): string {
    return intakeUnchangedLabels(this.mail ? intakeFields(this.mail) : {}, this.updateRows)
      .map(k => this.translate.instant(k))
      .join(', ');
  }

  markApplied(): Promise<void> {
    return this.run(() => this.intake.updateState(this.mail.id, 'applied'));
  }

  ignoreUpdate(): Promise<void> {
    return this.run(() => this.intake.updateState(this.mail.id, 'ignored'));
  }

  /** On a Direct request this deletes the vacancy (spec 5.6), so it asks first; a 409 means it is in use. */
  notARole(): Promise<void> {
    if (!this.canOverturn) { return Promise.resolve(); }
    return this.run(async () => {
      const deletes = this.mail.verdict === 'direct_request' && !!this.mail.vacancy;
      if (deletes) {
        const ok = await this.notifier.confirm(
          'intake.panel.not_a_role_confirm_title',
          this.translate.instant('intake.panel.not_a_role_confirm_body', { title: this.mail.vacancy?.title ?? '' }),
          'intake.panel.not_a_role_yes',
          'danger',
          'no',
        );
        if (!ok) { return null; }
      }
      return this.intake.notARole(this.mail.id).catch(e => {
        if (e?.status === 409 && e?.error?.message === 'intake.errors.vacancy_in_use') {
          this.toastr.danger(intakeInUseMessage(this.translate, e), this.translate.instant('intake.panel.not_a_role'));
          return null;
        }
        throw e;
      });
    });
  }

  /** create: Make or Create Direct request, prefilled from the mail. edit: Edit details of the stored vacancy. */
  openForm(mode: 'create' | 'edit'): Promise<void> {
    return this.run(async () => {
      // Edit without the stored vacancy would PUT every field as null and wipe it.
      if (mode === 'edit' && !this.vacancy) { return null; }
      const ref = this.dialog.open(CreateVacancyDialogComponent, {
        // Same width as Market's own create dialog; without it company and tariff clip.
        dialogClass: 'wide-vacancy-dialog',
        hasBackdrop: true,
        closeOnEsc: true,
        autoFocus: false,
        context: {
          intakeMode: mode,
          intakeMailId: this.mail.id,
          initialValues: intakeInitialValues(mode === 'edit' ? this.vacancy : intakeFields(this.mail)),
        },
      });
      const result = await ref.onClose.toPromise();
      // The dialog saw state_changed: someone else acted first, so show what holds now.
      if (result?.stateChanged) { return this.actionFailed({ status: 409, error: { message: 'intake.errors.state_changed' } }); }
      // Cancel closes with null; only the intake submit closes with a mail.
      return result && typeof result === 'object' && 'verdict' in result ? result as IntakeMail : null;
    });
  }

  chooseMatch(): Promise<void> {
    if (this.chosen === 'none') {
      return this.openForm('create');
    }
    const vacancyId = this.chosen;
    if (typeof vacancyId !== 'number') { return Promise.resolve(); }
    return this.run(() => this.intake.chooseMatch(this.mail.id, vacancyId));
  }

  async setBuyer(buyerId: number | null): Promise<void> {
    const before = this.mail.vacancy?.buyer_Client_id ?? null;
    if (!buyerId || buyerId === before) { return; }
    let linked = false;
    await this.run(async () => {
      const fresh = await this.intake.updateVacancy(this.mail.id, { buyer_Client_id: buyerId })
        .catch(e => this.actionFailed(e, this.translate.instant('intake.panel.crm_client')));
      linked = !!fresh;
      return fresh;
    });
    if (!linked) {
      this.selectedBuyer = before;
    }
  }

  async newProspect(): Promise<void> {
    if (this.busy) { return; }
    const ref = this.dialog.open(NewClientDialogComponent, {
      hasBackdrop: true,
      closeOnEsc: true,
      autoFocus: false,
      context: { navigateOnCreate: false, intake: true, initialQuery: intakeFields(this.mail).company ?? null },
    });
    const result = await ref.onClose.toPromise();
    if (!result?.clientId) { return; }
    await this.searchBuyers(result.name ?? '');
    this.selectedBuyer = result.clientId;
    await this.setBuyer(result.clientId);
  }
}
